import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Keeps the "OZCO Manufacturer Freight" cart line in step with the OZCO items in the cart.
 * The amount is calculated server-side in snippets/ozco-freight-state.liquid; this script only
 * reads that state and sets the freight variant quantities to match.
 */

const SOURCE_ID = 'ozco-freight';
const root = window.Shopify?.routes?.root || '/';

/** @type {Promise<boolean> | null} */
let pending = null;

async function readState() {
  const response = await fetch(`${root}?sections=ozco-freight-state`, { cache: 'no-store' });
  const sections = await response.json();
  const html = sections['ozco-freight-state'];
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const json = doc.querySelector('[data-ozco-freight-state]')?.textContent;
  return json ? JSON.parse(json) : null;
}

async function reconcile() {
  const state = await readState();
  if (!state || !state.ready || state.inSync) return false;

  const updates = {};
  if (state.have.flat !== state.want.flat) updates[state.flat] = state.want.flat;
  if (state.have.per !== state.want.per) updates[state.per] = state.want.per;

  const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates }),
  });
  const cart = await response.json();

  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE_ID, { source: SOURCE_ID, itemCount: cart.item_count })
  );
  return true;
}

/** Runs one reconcile at a time; callers share the in-flight run. */
function sync() {
  if (!pending) {
    pending = reconcile()
      .catch((error) => {
        console.error('OZCO freight sync failed', error);
        return false;
      })
      .finally(() => {
        pending = null;
      });
  }
  return pending;
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.sourceId === SOURCE_ID) return;
  sync();
});

// Make sure the freight line is correct before leaving for checkout.
document.addEventListener(
  'click',
  async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[name="checkout"]') : null;
    if (!button || button.dataset.ozcoFreightChecked) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    button.setAttribute('aria-busy', 'true');
    await (pending || Promise.resolve());
    await sync();
    button.dataset.ozcoFreightChecked = 'true';
    window.location.href = `${root}checkout`;
  },
  true
);

const loader = document.querySelector('script[data-ozco-freight]');
if (loader?.dataset.cartCount !== '0') sync();
